import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
    enableShare,
    disableShare,
    fetchChatById,
} from "../../services/ChatHistoryService";

function ShareButton({ conversationId, isObjectIdFn }) {
    const [open, setOpen] = useState(false);
    const [shared, setShared] = useState(false);
    const [shareId, setShareId] = useState(null);
    const [loading, setLoading] = useState(false);

    // Khi modal mở, fetch trạng thái share hiện tại
    useEffect(() => {
        if (!open) return;
        if (!isObjectIdFn(conversationId)) return;
        (async () => {
            try {
                const res = await fetchChatById(conversationId);
                const conv = res?.data || {};
                setShared(!!conv.shared);
                setShareId(conv.shareId || null);
            } catch (e) {
                console.error("fetch share state:", e);
            }
        })();
    }, [open, conversationId, isObjectIdFn]);

    const handleEnable = async () => {
        setLoading(true);
        try {
            const res = await enableShare(conversationId);
            const data = res?.data || {};
            setShared(true);
            setShareId(data.shareId);
            toast.success("Đã bật chia sẻ");
        } catch (e) {
            toast.error(e?.response?.data?.message || "Không bật được chia sẻ");
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async () => {
        setLoading(true);
        try {
            await disableShare(conversationId);
            setShared(false);
            setShareId(null);
            toast.success("Đã tắt chia sẻ");
        } catch (e) {
            toast.error(e?.response?.data?.message || "Không tắt được chia sẻ");
        } finally {
            setLoading(false);
        }
    };

    const shareUrl = shareId
        ? `${window.location.origin}/share/${shareId}`
        : "";

    const handleCopyUrl = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Đã sao chép liên kết");
        } catch {
            toast.error("Không copy được");
        }
    };

    if (!isObjectIdFn(conversationId)) {
        return null; // Không share được khi conversation chưa lưu
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="pim-header-icon-btn"
                title="Chia sẻ"
                aria-label="Chia sẻ cuộc trò chuyện"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
            </button>

            {open &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
                        onClick={() => !loading && setOpen(false)}
                    >
                        <div
                            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                            style={{ animation: "pimSlideUp 0.15s ease-out" }}
                        >
                            <div className="px-5 py-4 border-b border-gray-100">
                                <h3 className="text-base font-semibold text-gray-900">
                                    Chia sẻ cuộc trò chuyện
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    Bất kỳ ai có liên kết đều xem được nội dung dưới dạng chỉ đọc,
                                    không cần đăng nhập.
                                </p>
                            </div>

                            <div className="p-5 space-y-3">
                                {shared && shareId ? (
                                    <>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                                            Liên kết công khai
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                readOnly
                                                value={shareUrl}
                                                onFocus={(e) => e.target.select()}
                                                className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg bg-gray-50"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleCopyUrl}
                                                className="px-3 py-2 text-xs font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg"
                                            >
                                                Sao chép
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleDisable}
                                            disabled={loading}
                                            className="w-full px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                                        >
                                            Tắt chia sẻ
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleEnable}
                                        disabled={loading}
                                        className="w-full px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                                    >
                                        {loading ? "Đang tạo…" : "Tạo liên kết chia sẻ"}
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center justify-end px-5 py-3 border-t border-gray-100 bg-gray-50">
                                <button
                                    type="button"
                                    disabled={loading}
                                    onClick={() => setOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                    Đóng
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}

export default ShareButton;
